import { useState, useMemo, useEffect, useCallback } from "react";
import { useAccounting } from "@/contexts/AccountingContext";
import { useCompany } from "@/contexts/CompanyContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { EWayBill, EWayBillStatus, TransportMode, SupplyType, SubSupplyType, DocumentType, calculateEWayValidity } from "@/lib/accounting/types";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Plus, Search, Download, Truck, Clock, CheckCircle2, XCircle,
  AlertTriangle, MapPin, ChevronDown, ChevronRight, FileText, Calendar, Package, Printer, RefreshCw, Copy, ExternalLink, ShieldCheck
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { EWayBillSlip } from "@/components/accounting/EWayBillSlip";
import { buildEWayBillGovtJson, downloadJsonFile, getStateCode, formatDateGovt, EWayBillExportData } from "@/lib/gst/govJsonExporter";

const INDIAN_STATES = [
  "Punjab", "Haryana", "Delhi", "Chandigarh", "Himachal Pradesh", "Rajasthan",
  "Uttar Pradesh", "Uttarakhand", "Jammu & Kashmir", "Maharashtra", "Gujarat",
  "Karnataka", "Tamil Nadu", "West Bengal", "Bihar", "Madhya Pradesh", "Andhra Pradesh",
  "Telangana", "Kerala", "Odisha", "Assam", "Goa", "Jharkhand", "Chhattisgarh"
];

const TRANSPORT_MODES: TransportMode[] = ["Road", "Rail", "Air", "Ship"];
const SUPPLY_TYPES: SupplyType[] = ["Outward", "Inward"];
const SUB_SUPPLY_TYPES: SubSupplyType[] = ["Supply", "Export", "Job Work", "SKD/CKD", "Recipient Not Known", "Line Sales", "Sales Return", "Exhibition", "Others"];
const DOC_TYPES: DocumentType[] = ["Tax Invoice", "Bill of Supply", "Bill of Entry", "Challan", "Credit Note", "Debit Note", "Others"];

const statusConfig: Record<EWayBillStatus, { icon: typeof CheckCircle2; className: string }> = {
  Active: { icon: CheckCircle2, className: "text-green-600 bg-green-500/10 border-green-200" },
  Draft: { icon: Clock, className: "text-neutral-500 bg-neutral-100 border-neutral-200" },
  Expired: { icon: AlertTriangle, className: "text-amber-600 bg-amber-500/10 border-amber-200" },
  Cancelled: { icon: XCircle, className: "text-destructive bg-destructive/10 border-destructive/20" },
};

function mapDbEWayBill(r: any): EWayBill {
  return {
    id: r.id,
    ewayBillNumber: r.eway_bill_number,
    status: r.status,
    supplyType: r.supply_type,
    subSupplyType: r.sub_supply_type,
    documentType: r.document_type,
    voucherId: r.voucher_id,
    voucherNumber: r.doc_number,
    invoiceNumber: r.doc_number,
    invoiceDate: r.doc_date,
    invoiceValue: Number(r.total_value),
    fromGstin: r.from_gstin,
    fromName: r.from_name,
    fromAddress: r.from_address,
    fromState: r.from_state,
    fromPincode: r.from_pincode,
    toGstin: r.to_gstin,
    toName: r.to_name,
    toAddress: r.to_address,
    toState: r.to_state,
    toPincode: r.to_pincode,
    hsnCode: r.hsn_code,
    productName: r.product_name,
    quantity: Number(r.quantity),
    unit: r.unit,
    taxableValue: Number(r.taxable_value),
    cgstRate: Number(r.cgst_rate),
    sgstRate: Number(r.sgst_rate),
    igstRate: Number(r.igst_rate),
    cgstAmount: Number(r.cgst_amount),
    sgstAmount: Number(r.sgst_amount),
    igstAmount: Number(r.igst_amount),
    cessAmount: Number(r.cess_amount),
    transportMode: r.transport_mode,
    transporterId: r.transporter_id,
    transporterName: r.transporter_name,
    transportDocNumber: r.transport_doc_number,
    vehicleNumber: r.vehicle_number,
    vehicleType: r.vehicle_type,
    distanceKm: Number(r.distance_km),
    validFrom: r.valid_from,
    validUntil: r.valid_until,
    generatedAt: r.created_at,
    generatedBy: r.created_by,
    updatedAt: r.updated_at,
    remarks: r.remarks,
  };
}

export const EWayBillsPage = () => {
  const { vouchers, ledgers } = useAccounting();
  const { activeCompany } = useCompany();
  const { user } = useAuth();
  const { toast } = useToast();

  const [ewayBills, setEwayBills] = useState<EWayBill[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [printBill, setPrintBill] = useState<EWayBill | null>(null);
  const [printOpen, setPrintOpen] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    voucherId: "",
    customEwbNumber: "",
    supplyType: "Outward" as SupplyType,
    subSupplyType: "Supply" as SubSupplyType,
    documentType: "Tax Invoice" as DocumentType,
    toName: "",
    toGstin: "",
    toAddress: "",
    toState: "Punjab",
    toPincode: "147301",
    hsnCode: "61091000",
    productName: "Custom Printed Merchandise",
    quantity: 1,
    unit: "Pcs",
    taxableValue: 0,
    cgstAmount: 0,
    sgstAmount: 0,
    igstAmount: 0,
    totalValue: 0,
    transportMode: "Road" as TransportMode,
    transporterName: "BlueDart Express Logistics",
    transporterId: "03AAACB1234F1Z8",
    transportDocNumber: "BLU-889124",
    vehicleNumber: "PB 23 AA 4567",
    vehicleType: "Regular" as "Regular" | "Over Dimensional Cargo",
    distanceKm: 85,
    remarks: "Goods dispatched for delivery",
  });

  const companyGstin = activeCompany?.gstin || "22FZVPS1799R5";
  const companyName = activeCompany?.name || "hSECURITIES";
  const companyAddress = activeCompany?.address || "GT Road, Mandi Gobindgarh";
  const companyState = activeCompany?.state || "Punjab";
  const companyPincode = activeCompany?.pincode || "147301";

  // Load from MySQL
  const loadEWayBills = useCallback(async () => {
    if (!activeCompany?.id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("eway_bills")
        .select("*")
        .eq("company_id", activeCompany.id)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error loading eway bills:", error);
      } else if (data) {
        setEwayBills(data.map(mapDbEWayBill));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [activeCompany?.id]);

  useEffect(() => {
    loadEWayBills();
  }, [loadEWayBills]);

  // Eligible vouchers (Sales & Purchase)
  const eligibleVouchers = useMemo(() => {
    return vouchers.filter(
      (v) => (v.type === "Sales" || v.type === "Purchase") && v.status === "Approved"
    );
  }, [vouchers]);

  const filtered = useMemo(() => {
    return ewayBills.filter((ew) => {
      const matchSearch =
        ew.ewayBillNumber.includes(search) ||
        ew.toName.toLowerCase().includes(search.toLowerCase()) ||
        ew.productName.toLowerCase().includes(search.toLowerCase()) ||
        (ew.vehicleNumber && ew.vehicleNumber.toLowerCase().includes(search.toLowerCase())) ||
        (ew.voucherNumber && ew.voucherNumber.toLowerCase().includes(search.toLowerCase()));
      const matchStatus = statusFilter === "all" || ew.status === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [ewayBills, search, statusFilter]);

  const stats = useMemo(() => ({
    total: ewayBills.length,
    active: ewayBills.filter((e) => e.status === "Active").length,
    expired: ewayBills.filter((e) => e.status === "Expired").length,
    totalValue: ewayBills.filter((e) => e.status === "Active").reduce((s, e) => s + e.invoiceValue, 0),
  }), [ewayBills]);

  const handleVoucherSelect = (voucherId: string) => {
    const v = vouchers.find((vch) => vch.id === voucherId);
    if (!v) return;
    const party = v.partyLedgerId ? ledgers.find((l) => l.id === v.partyLedgerId) : undefined;
    
    // Extract first item or defaults
    const firstItem = v.items && v.items.length > 0 ? v.items[0] : null;
    const totTaxable = v.items && v.items.length > 0 
      ? v.items.reduce((s, it) => s + it.taxableAmount, 0)
      : (v.totalDebit - (v.gstAmount || 0));

    setFormData((prev) => ({
      ...prev,
      voucherId,
      supplyType: v.type === "Sales" ? "Outward" : "Inward",
      toName: party?.name || v.partyName || "Walk-in Customer",
      toGstin: party?.gstin || "",
      toState: party?.state || "Punjab",
      toAddress: party?.address || "Mandi Gobindgarh, Punjab",
      toPincode: party?.pincode || "147301",
      productName: firstItem ? firstItem.name : "Custom Printed Merchandise",
      hsnCode: firstItem ? firstItem.hsn : "61091000",
      quantity: firstItem ? firstItem.quantity : 1,
      unit: firstItem ? firstItem.unit : "Pcs",
      taxableValue: totTaxable,
      cgstAmount: v.cgst || 0,
      sgstAmount: v.sgst || 0,
      igstAmount: v.igst || 0,
      totalValue: v.totalDebit,
    }));
  };

  const handleGenerate = async () => {
    if (!formData.voucherId) {
      toast({ title: "Error", description: "Please select an invoice/voucher", variant: "destructive" });
      return;
    }
    if (!formData.toName || !formData.toState || !formData.toPincode) {
      toast({ title: "Error", description: "Complete consignee details required", variant: "destructive" });
      return;
    }
    if (formData.distanceKm <= 0) {
      toast({ title: "Error", description: "Distance must be greater than 0", variant: "destructive" });
      return;
    }

    const v = vouchers.find((vch) => vch.id === formData.voucherId)!;
    const validDays = calculateEWayValidity(formData.distanceKm);
    const now = new Date();
    const validUntil = new Date(now);
    validUntil.setDate(validUntil.getDate() + validDays);

    // If user provided a custom 12-digit number from Govt portal, use it; otherwise generate standard 12-digit format
    let billNumber = formData.customEwbNumber.trim().replace(/\s+/g, '');
    if (!billNumber || billNumber.length < 10) {
      const part1 = Math.floor(1000 + Math.random() * 9000);
      const part2 = Math.floor(1000 + Math.random() * 9000);
      const part3 = Math.floor(1000 + Math.random() * 9000);
      billNumber = `${part1} ${part2} ${part3}`;
    } else {
      billNumber = `${billNumber.slice(0, 4)} ${billNumber.slice(4, 8)} ${billNumber.slice(8, 12)}`;
    }

    const newId = `ewb-${crypto.randomUUID().slice(0, 8)}`;
    const dbRow = {
      id: newId,
      company_id: activeCompany?.id || "comp-mufjspek",
      voucher_id: v.id,
      eway_bill_number: billNumber,
      status: "Active",
      supply_type: formData.supplyType,
      sub_supply_type: formData.subSupplyType,
      document_type: formData.documentType,
      doc_number: v.invoiceNumber || v.voucherNumber,
      doc_date: v.date,
      from_gstin: companyGstin,
      from_name: companyName,
      from_address: companyAddress,
      from_state: companyState,
      from_pincode: companyPincode,
      to_gstin: formData.toGstin || null,
      to_name: formData.toName,
      to_address: formData.toAddress,
      to_state: formData.toState,
      to_pincode: formData.toPincode,
      hsn_code: formData.hsnCode,
      product_name: formData.productName,
      quantity: formData.quantity,
      unit: formData.unit,
      taxable_value: formData.taxableValue,
      cgst_rate: v.cgst ? 9 : 0,
      sgst_rate: v.sgst ? 9 : 0,
      igst_rate: v.igst ? 18 : 0,
      cgst_amount: formData.cgstAmount,
      sgst_amount: formData.sgstAmount,
      igst_amount: formData.igstAmount,
      cess_amount: 0,
      total_value: formData.totalValue,
      transport_mode: formData.transportMode,
      transporter_id: formData.transporterId,
      transporter_name: formData.transporterName,
      transport_doc_number: formData.transportDocNumber,
      transport_doc_date: v.date,
      vehicle_number: formData.vehicleNumber,
      vehicle_type: formData.vehicleType,
      distance_km: formData.distanceKm,
      valid_from: now.toISOString().slice(0, 19).replace('T', ' '),
      valid_until: validUntil.toISOString().slice(0, 19).replace('T', ' '),
      remarks: formData.remarks,
      created_by: user?.id || "usr-admin-default",
    };

    try {
      // 1. Save to eway_bills table
      const { error: insErr } = await supabase.from("eway_bills").insert(dbRow);
      if (insErr) throw insErr;

      // 2. Sync to vouchers table
      await supabase
        .from("vouchers")
        .update({
          eway_bill_no: billNumber,
          eway_bill_valid_until: validUntil.toISOString().slice(0, 19).replace('T', ' '),
        })
        .eq("id", v.id);

      toast({
        title: "✅ E-Way Bill Generated & Saved",
        description: `E-Way Bill ${billNumber} generated for ${v.invoiceNumber || v.voucherNumber}. Form GST EWB-01 is ready to print.`,
      });

      setFormOpen(false);
      loadEWayBills();
    } catch (err: any) {
      console.error(err);
      toast({ title: "Failed to Save", description: err.message, variant: "destructive" });
    }
  };

  const handleExportBulkGovtJson = () => {
    if (ewayBills.length === 0) {
      toast({ title: "No E-Way Bills", description: "Generate or select at least one E-Way Bill to export." });
      return;
    }

    const payloadBills: EWayBillExportData[] = ewayBills.map((ew) => {
      const fromStCode = getStateCode(ew.fromState, ew.fromGstin);
      const toStCode = getStateCode(ew.toState, ew.toGstin);

      return {
        userGstin: ew.fromGstin,
        supplyType: ew.supplyType === "Outward" ? "O" : "I",
        subSupplyType: 1, // Supply
        docType: "INV",
        docNo: ew.invoiceNumber || ew.voucherNumber || "INV-01",
        docDate: formatDateGovt(ew.invoiceDate),
        fromGstin: ew.fromGstin,
        fromTrdName: ew.fromName,
        fromAddr1: ew.fromAddress || "GT Road",
        fromPlace: ew.fromState || "Punjab",
        fromPincode: Number(ew.fromPincode) || 147301,
        fromStateCode: fromStCode,
        toGstin: ew.toGstin || "URP",
        toTrdName: ew.toName,
        toAddr1: ew.toAddress || "Punjab",
        toPlace: ew.toState || "Punjab",
        toPincode: Number(ew.toPincode) || 147301,
        toStateCode: toStCode,
        totalValue: ew.taxableValue,
        cgstValue: ew.cgstAmount,
        sgstValue: ew.sgstAmount,
        igstValue: ew.igstAmount,
        cessValue: 0,
        totInvValue: ew.invoiceValue,
        transMode: ew.transportMode === "Road" ? 1 : ew.transportMode === "Rail" ? 2 : ew.transportMode === "Air" ? 3 : 4,
        transDistance: ew.distanceKm,
        transporterName: ew.transporterName,
        transporterId: ew.transporterId,
        transDocNo: ew.transportDocNumber,
        vehicleNo: ew.vehicleNumber?.replace(/\s+/g, ''),
        vehicleType: ew.vehicleType === "Over Dimensional Cargo" ? "O" : "R",
        itemList: [
          {
            itemNo: 1,
            productName: ew.productName,
            productDesc: ew.productName,
            hsnCode: Number(ew.hsnCode) || 61091000,
            quantity: ew.quantity,
            qtyUnit: ew.unit || "PCS",
            cgstRate: ew.cgstRate,
            sgstRate: ew.sgstRate,
            igstRate: ew.igstRate,
            cessRate: 0,
            taxableAmount: ew.taxableValue,
          },
        ],
      };
    });

    const fullJson = buildEWayBillGovtJson(payloadBills);
    downloadJsonFile(`EWayBills_Bulk_Upload_${companyGstin}.json`, fullJson);

    toast({
      title: "📥 Govt JSON File Downloaded",
      description: "Upload this file directly to ewaybillgst.gov.in under 'Generate Bulk' to issue government bills.",
    });
  };

  const handleCancelBill = async (bill: EWayBill) => {
    try {
      await supabase
        .from("eway_bills")
        .update({ status: "Cancelled" })
        .eq("id", bill.id);

      toast({
        title: "E-Way Bill Cancelled",
        description: `E-Way Bill ${bill.ewayBillNumber} marked as Cancelled.`,
      });
      loadEWayBills();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground flex items-center gap-2">
            <Truck className="h-6 w-6 text-primary" /> E-Way Bill Management
          </h1>
          <p className="text-sm text-muted-foreground">
            Generate, track, and export official Form GST EWB-01 for goods transport & logistics
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleExportBulkGovtJson} className="gap-1.5 border-primary/30 text-primary hover:bg-primary/5">
            <Download className="h-4 w-4" /> Export Govt JSON (Offline Tool)
          </Button>

          <Dialog open={formOpen} onOpenChange={setFormOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-1.5">
                <Plus className="h-4 w-4" /> New E-Way Bill
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Truck className="h-5 w-5 text-primary" /> Generate E-Way Bill (Form GST EWB-01)
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4 pt-2 text-xs">
                {/* Consignor info banner */}
                <div className="p-3 bg-muted rounded-md flex justify-between items-center text-xs">
                  <div>
                    <span className="font-bold text-foreground">Consignor: {companyName}</span>
                    <p className="text-muted-foreground font-mono">GSTIN: {companyGstin} | State: {companyState}</p>
                  </div>
                  <ShieldCheck className="h-5 w-5 text-green-600" />
                </div>

                {/* Voucher select */}
                <div>
                  <label className="block font-semibold mb-1">Select Sales/Purchase Invoice *</label>
                  <Select value={formData.voucherId} onValueChange={handleVoucherSelect}>
                    <SelectTrigger className="w-full text-xs">
                      <SelectValue placeholder="Choose an approved invoice..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {eligibleVouchers.slice(0, 50).map((v) => (
                        <SelectItem key={v.id} value={v.id} className="text-xs">
                          {v.invoiceNumber || v.voucherNumber} — {v.partyName || "Party"} (₹{v.totalDebit.toLocaleString("en-IN")}) — {v.date}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Optional Govt EWB Number */}
                <div>
                  <label className="block font-semibold mb-1">
                    12-Digit Govt E-Way Bill Number (Optional)
                  </label>
                  <input
                    type="text"
                    value={formData.customEwbNumber}
                    onChange={(e) => setFormData({ ...formData, customEwbNumber: e.target.value })}
                    placeholder="Leave empty to auto-generate, or paste 12-digit number from ewaybillgst.gov.in"
                    className="w-full bg-card border border-border rounded px-3 py-1.5 text-xs font-mono outline-none focus:border-primary"
                  />
                  <span className="text-[10px] text-muted-foreground block mt-0.5">
                    Agar aapne portal par direct generate kiya hai toh yahan 12-digit number paste kar sakte hain.
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block font-semibold mb-1">Consignee / Party Name *</label>
                    <input
                      type="text"
                      value={formData.toName}
                      onChange={(e) => setFormData({ ...formData, toName: e.target.value })}
                      className="w-full bg-card border border-border rounded px-2.5 py-1.5 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block font-semibold mb-1">Consignee GSTIN (Blank if URP)</label>
                    <input
                      type="text"
                      value={formData.toGstin}
                      onChange={(e) => setFormData({ ...formData, toGstin: e.target.value })}
                      placeholder="e.g. 03AAACA1234A1Z5"
                      className="w-full bg-card border border-border rounded px-2.5 py-1.5 text-xs font-mono"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <label className="block font-semibold mb-1">Delivery Address *</label>
                    <input
                      type="text"
                      value={formData.toAddress}
                      onChange={(e) => setFormData({ ...formData, toAddress: e.target.value })}
                      className="w-full bg-card border border-border rounded px-2.5 py-1.5 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block font-semibold mb-1">Delivery Pincode *</label>
                    <input
                      type="text"
                      value={formData.toPincode}
                      onChange={(e) => setFormData({ ...formData, toPincode: e.target.value })}
                      className="w-full bg-card border border-border rounded px-2.5 py-1.5 text-xs font-mono"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block font-semibold mb-1">Product Name</label>
                    <input
                      type="text"
                      value={formData.productName}
                      onChange={(e) => setFormData({ ...formData, productName: e.target.value })}
                      className="w-full bg-card border border-border rounded px-2.5 py-1.5 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block font-semibold mb-1">HSN Code</label>
                    <input
                      type="text"
                      value={formData.hsnCode}
                      onChange={(e) => setFormData({ ...formData, hsnCode: e.target.value })}
                      className="w-full bg-card border border-border rounded px-2.5 py-1.5 text-xs font-mono"
                    />
                  </div>
                  <div>
                    <label className="block font-semibold mb-1">Total Invoice Value (₹)</label>
                    <input
                      type="number"
                      value={formData.totalValue}
                      disabled
                      className="w-full bg-muted border border-border rounded px-2.5 py-1.5 text-xs font-bold"
                    />
                  </div>
                </div>

                <div className="border-t border-border pt-3">
                  <h4 className="font-bold text-xs uppercase tracking-wider mb-2 text-primary">Part-B Transport Details</h4>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block font-semibold mb-1">Transport Mode</label>
                      <Select value={formData.transportMode} onValueChange={(v) => setFormData({ ...formData, transportMode: v as TransportMode })}>
                        <SelectTrigger className="w-full text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {TRANSPORT_MODES.map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block font-semibold mb-1">Vehicle Number *</label>
                      <input
                        type="text"
                        value={formData.vehicleNumber}
                        onChange={(e) => setFormData({ ...formData, vehicleNumber: e.target.value.toUpperCase() })}
                        placeholder="e.g. PB 23 AA 1234"
                        className="w-full bg-card border border-border rounded px-2.5 py-1.5 text-xs font-mono uppercase"
                      />
                    </div>
                    <div>
                      <label className="block font-semibold mb-1">Distance (KM) *</label>
                      <input
                        type="number"
                        value={formData.distanceKm}
                        onChange={(e) => setFormData({ ...formData, distanceKm: Number(e.target.value) })}
                        className="w-full bg-card border border-border rounded px-2.5 py-1.5 text-xs font-mono"
                      />
                      <span className="text-[10px] text-muted-foreground block mt-0.5">
                        Validity: {calculateEWayValidity(formData.distanceKm)} day(s)
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <label className="block font-semibold mb-1">Transporter Name / Partner</label>
                      <input
                        type="text"
                        value={formData.transporterName}
                        onChange={(e) => setFormData({ ...formData, transporterName: e.target.value })}
                        className="w-full bg-card border border-border rounded px-2.5 py-1.5 text-xs"
                      />
                    </div>
                    <div>
                      <label className="block font-semibold mb-1">Transporter Doc / LR Number</label>
                      <input
                        type="text"
                        value={formData.transportDocNumber}
                        onChange={(e) => setFormData({ ...formData, transportDocNumber: e.target.value })}
                        className="w-full bg-card border border-border rounded px-2.5 py-1.5 text-xs font-mono"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-3 border-t border-border">
                  <Button variant="outline" size="sm" onClick={() => setFormOpen(false)}>Cancel</Button>
                  <Button size="sm" onClick={handleGenerate}>Save & Generate Slip</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total E-Way Bills", value: stats.total, icon: FileText, color: "text-foreground" },
          { label: "Active Bills", value: stats.active, icon: CheckCircle2, color: "text-green-600" },
          { label: "Expired", value: stats.expired, icon: AlertTriangle, color: "text-amber-600" },
          { label: "Total Active Value", value: `₹${stats.totalValue.toLocaleString("en-IN")}`, icon: Truck, color: "text-primary" },
        ].map((s) => (
          <div key={s.label} className="bg-card border border-border rounded-lg p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <s.icon className={`h-4 w-4 ${s.color}`} />
              <span className="text-xs text-muted-foreground">{s.label}</span>
            </div>
            <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Search and Filters */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 bg-card border border-border rounded-md px-3 py-1.5 flex-1 max-w-sm shadow-sm">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by EWB, party, invoice, vehicle..."
            className="bg-transparent text-sm outline-none w-full text-foreground placeholder:text-muted-foreground"
          />
        </div>
        <div className="flex items-center gap-1.5">
          {["all", "Active", "Expired", "Cancelled"].map((st) => (
            <button
              key={st}
              onClick={() => setStatusFilter(st)}
              className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                statusFilter === st
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              }`}
            >
              {st === "all" ? "All Bills" : st}
            </button>
          ))}
        </div>
        <span className="text-xs text-muted-foreground ml-auto">{filtered.length} results</span>
      </div>

      {/* E-Way Bills Table */}
      <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 border-b border-border text-xs text-muted-foreground uppercase font-semibold">
                <th className="w-8 py-3 px-4"></th>
                <th className="text-left py-3 px-4">E-Way Bill No.</th>
                <th className="text-left py-3 px-4">Document</th>
                <th className="text-left py-3 px-4">Recipient / Party</th>
                <th className="text-left py-3 px-4">Vehicle No.</th>
                <th className="text-right py-3 px-4">Amount (₹)</th>
                <th className="text-left py-3 px-4">Validity</th>
                <th className="text-left py-3 px-4">Status</th>
                <th className="text-right py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-10 text-muted-foreground">
                    <Truck className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    No E-Way Bills found. Click <strong>"New E-Way Bill"</strong> to generate one.
                  </td>
                </tr>
              ) : (
                filtered.map((ew) => {
                  const isExpanded = expandedId === ew.id;
                  const sc = statusConfig[ew.status] || statusConfig.Active;
                  const StatusIcon = sc.icon;

                  return (
                    <tr
                      key={ew.id}
                      className="hover:bg-muted/30 transition-colors cursor-pointer"
                      onClick={() => setExpandedId(isExpanded ? null : ew.id)}
                    >
                      <td className="py-3 px-4">
                        {isExpanded ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-mono font-bold text-primary">{ew.ewayBillNumber}</span>
                        <span className="text-[11px] text-muted-foreground block">{ew.documentType}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-medium text-foreground">{ew.invoiceNumber || ew.voucherNumber}</span>
                        <span className="text-[11px] text-muted-foreground block">{ew.invoiceDate}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-medium text-foreground">{ew.toName}</span>
                        <span className="text-[11px] text-muted-foreground block font-mono">{ew.toGstin || "URP"} | {ew.toState}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-mono font-semibold text-foreground">{ew.vehicleNumber || "—"}</span>
                        <span className="text-[11px] text-muted-foreground block">{ew.transportMode} ({ew.distanceKm} KM)</span>
                      </td>
                      <td className="py-3 px-4 text-right font-bold text-foreground">
                        ₹{ew.invoiceValue.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </td>
                      <td className="py-3 px-4 text-xs">
                        <span className="text-muted-foreground block">Until: {new Date(ew.validUntil).toLocaleDateString("en-IN", { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${sc.className}`}>
                          <StatusIcon className="h-3 w-3" />
                          {ew.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1.5">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-xs gap-1"
                            onClick={() => {
                              setPrintBill(ew);
                              setPrintOpen(true);
                            }}
                          >
                            <Printer className="h-3.5 w-3.5 text-primary" /> Print Slip
                          </Button>
                          {ew.status === "Active" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-xs text-destructive hover:bg-destructive/10"
                              onClick={() => handleCancelBill(ew)}
                            >
                              Cancel
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Official Printable Slip Modal */}
      <EWayBillSlip
        bill={printBill}
        open={printOpen}
        onOpenChange={setPrintOpen}
      />
    </div>
  );
};

export default EWayBillsPage;
